import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function presetRange(key, today = new Date()) {
  const y = today.getFullYear();
  if (key === "all") return { from: "", to: "" };
  if (key === "year") return { from: `${y}-01-01`, to: `${y}-12-31` };
  if (key === "prev") return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  const f = new Date(today.getFullYear(), today.getMonth() - 12, today.getDate() + 1);
  return { from: iso(f), to: iso(today) };
}

const PRESETS = [
  { key: "all", label: "Gesamter Zeitraum" }, { key: "year", label: "Laufendes Jahr" },
  { key: "prev", label: "Vorjahr" }, { key: "12m", label: "Letzte 12 Monate" },
];

// Editor-Button: Mietkonto als PDF erzeugen und als Anlage hinzufügen
export default function AccountStatementAttach({ tenancyId, onAttached }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pick = (k) => { setPreset(k); setRange(presetRange(k)); };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenancies/${tenancyId}/account-statement/attach`, {
        from_date: range.from || null, to_date: range.to || null, only_open: onlyOpen,
      });
      onAttached(data);
      toast.success(`„${data.title}“ angehängt`);
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Mietkonto-PDF konnte nicht erstellt werden"); }
    finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-[10px] font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1"
          data-testid="attach-account-statement-btn">
          <Receipt size={12} /> Mietkonto
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3" data-testid="attach-account-statement-popover">
        <div className="text-sm font-semibold">Mietkonto als PDF anhängen</div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => pick(p.key)} data-testid={`stmt-preset-${p.key}`}
              className={`text-[11px] px-2 py-1 rounded-full border ${preset === p.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] text-slate-500">Von
            <Input type="date" value={range.from} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }}
              className="h-8 text-xs mt-0.5" data-testid="stmt-from" />
          </label>
          <label className="text-[11px] text-slate-500">Bis
            <Input type="date" value={range.to} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }}
              className="h-8 text-xs mt-0.5" data-testid="stmt-to" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox checked={onlyOpen} onCheckedChange={(v) => setOnlyOpen(!!v)} data-testid="stmt-only-open" />
          Nur offene Posten (z. B. als Anlage zur Mahnung)
        </label>
        <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={create} data-testid="stmt-attach-submit">
          {busy ? "Erstelle PDF…" : "PDF erstellen & anhängen"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
